import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChevronUp, ChevronDown, Trash2, Plus, GripVertical, Link2, RefreshCcw, Unlink, CheckCircle2, Palette } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function Settings() {
  const [stages, setStages] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2563EB");
  const [etsy, setEtsy] = useState(null);
  const [ekey, setEkey] = useState("");
  const [esec, setEsec] = useState("");
  const [eshop, setEshop] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [paints, setPaints] = useState([]);
  const [pLabel, setPLabel] = useState("");
  const [pHex, setPHex] = useState("#000000");

  const loadStages = useCallback(() => api.get("/stages").then((r) => setStages(r.data)), []);
  const loadEtsy = useCallback(() => api.get("/etsy/config").then((r) => setEtsy(r.data)), []);
  const loadPaints = useCallback(() => api.get("/paint-colors").then((r) => setPaints(r.data)), []);
  const paramHandled = useRef(false);

  useEffect(() => {
    loadStages();
    loadEtsy();
    loadPaints();
  }, [loadStages, loadEtsy, loadPaints]);

  useEffect(() => {
    if (paramHandled.current) return;
    if (params.get("etsy") === "connected") {
      toast.success("Etsy başarıyla bağlandı");
      paramHandled.current = true;
      setParams({});
    }
    const err = params.get("etsy_error");
    if (err) {
      toast.error(`Etsy bağlantı hatası: ${err}`);
      paramHandled.current = true;
      setParams({});
    }
  }, [params, setParams]);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/stages", { name, color });
    setName(""); setColor("#2563EB"); loadStages();
  };
  const del = async (s) => {
    if (!window.confirm(`"${s.name}" aşamasını silmek istediğinize emin misiniz?`)) return;
    await api.delete(`/stages/${s.id}`); loadStages();
  };
  const move = async (i, direction) => {
    const newList = [...stages];
    const j = i + direction;
    if (j < 0 || j >= newList.length) return;
    [newList[i], newList[j]] = [newList[j], newList[i]];
    setStages(newList);
    await api.post("/stages/reorder", { ids: newList.map((s) => s.id) });
    toast.success("Sıra güncellendi");
  };
  const rename = async (s, newName) => {
    await api.put(`/stages/${s.id}`, { name: newName, color: s.color });
    loadStages();
  };

  const saveEtsy = async () => {
    if (!ekey || !esec) return toast.error("API Key ve Shared Secret zorunlu");
    setBusy(true);
    try {
      await api.post("/etsy/config", { keystring: ekey.trim(), shared_secret: esec.trim(), shop_id: eshop ? Number(eshop) : null });
      setEkey(""); setEsec("");
      toast.success("Etsy anahtarları kaydedildi");
      loadEtsy();
    } finally { setBusy(false); }
  };
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/etsy/oauth/start");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bağlantı başlatılamadı");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Etsy bağlantısını kesmek istiyor musunuz?")) return;
    await api.post("/etsy/disconnect");
    toast.success("Bağlantı kesildi");
    loadEtsy();
  };
  const syncNow = async (full = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/etsy/sync", null, { params: full ? { full: true } : {} });
      if (data.error) toast.error(`Hata: ${data.error}`);
      else toast.success(`${data.checked ?? 0} sipariş kontrol edildi · ${data.imported ?? 0} yeni eklendi`);
      loadEtsy();
    } catch (e) { toast.error("Senkron hatası"); }
    finally { setBusy(false); }
  };
  const backfillImages = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/etsy/backfill-images");
      if (data.error) toast.error(`Hata: ${data.error}`);
      else toast.success(`${data.updated} ürün görseli güncellendi`);
    } catch (e) { toast.error("Görsel indirme hatası"); }
    finally { setBusy(false); }
  };

  const reconcile = async () => {
    if (!window.confirm("Etsy'de kargolanmış olan tüm siparişler 'Tamamlandı' statüsüne alınacak, yalnızca kargolanmamış olanlar üretim listesinde kalacak. Devam edilsin mi?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/etsy/reconcile");
      toast.success(`${data.checked} sipariş kontrol edildi · ${data.completed} tamamlandı · ${data.reactivated} yeniden aktif`);
      loadEtsy();
    } catch (e) { toast.error(e.response?.data?.detail || "Eşitleme hatası"); }
    finally { setBusy(false); }
  };

  const resetAndResync = async () => {
    if (!window.confirm("⚠️ TÜM MEVCUT SİPARİŞLER SİLİNECEK ve son 45 günün sadece kargolanmamış Etsy siparişleri yeniden çekilecek. Bu işlem geri alınamaz. Devam edilsin mi?")) return;
    if (!window.confirm("Emin misiniz? SON KEZ ONAY: Tüm siparişleri silmek için 'Tamam'a basın.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders/reset-and-resync", null, { params: { days: 45 } });
      if (data.error) toast.error(`Hata: ${data.error} · ${data.detail || ""}`);
      else toast.success(`${data.deleted} silindi · ${data.imported} yeni kargolanmamış sipariş çekildi (son ${data.days}g)`);
      loadEtsy();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem hatası"); }
    finally { setBusy(false); }
  };

  const [syncLog, setSyncLog] = useState(null);
  const loadLog = async () => {
    try {
      const { data } = await api.get("/etsy/sync-log", { params: { limit: 20 } });
      setSyncLog(data);
    } catch (e) { toast.error("Log okunamadı"); }
  };

  const addPaint = async () => {
    if (!pLabel.trim()) return toast.error("Etiket zorunlu");
    try {
      await api.post("/paint-colors", { label: pLabel.trim(), hex: pHex });
      setPLabel(""); setPHex("#000000");
      toast.success("Renk eklendi");
      loadPaints();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const updatePaint = async (p, patch) => {
    try {
      await api.put(`/paint-colors/${p.id}`, { label: patch.label ?? p.label, hex: patch.hex ?? p.hex });
      loadPaints();
    } catch (e) { toast.error("Hata"); }
  };
  const delPaint = async (p) => {
    if (!window.confirm(`"${p.label}" rengini silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/paint-colors/${p.id}`);
      toast.success("Silindi");
      loadPaints();
    } catch (e) { toast.error("Hata"); }
  };

  return (
    <div className="p-8">
      <PageHeader eyebrow="Yapılandırma" title="Ayarlar" subtitle="İmalat aşamaları ve Etsy entegrasyonu" />

      <Card className="p-6 border-slate-200 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="h-4 w-4 text-blue-600" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Kanal</div>
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Etsy Otomasyonu</h3>
        <p className="text-sm text-slate-500 mb-4">Yeni ödenmiş &amp; kargolanmamış siparişler her 15 dakikada bir otomatik çekilir ve Üretim Panosuna eklenir.</p>

        {etsy && (
          <div className="flex flex-wrap gap-3 mb-5">
            <StatusPill label="Anahtarlar" ok={etsy.configured} okText="Kayıtlı" noText="Eksik" />
            <StatusPill label="Bağlantı" ok={etsy.connected} okText="Bağlı" noText="Bağlı Değil" />
            <StatusPill label="Shop ID" value={etsy.shop_id || "—"} />
            <StatusPill label="Son Senkron" value={etsy.last_sync_at ? new Date(etsy.last_sync_at).toLocaleString("tr-TR") : "—"} />
            <StatusPill label="Son Çekilen" value={etsy.last_sync_count ?? 0} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600 font-bold">API Keystring</Label>
            <Input data-testid="etsy-keystring" value={ekey} onChange={(e) => setEkey(e.target.value)} placeholder={etsy?.keystring_masked || "abcd12ef34..."} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600 font-bold">Shared Secret</Label>
            <Input data-testid="etsy-secret" type="password" value={esec} onChange={(e) => setEsec(e.target.value)} placeholder="••••••••" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600 font-bold">Shop ID (opsiyonel)</Label>
            <Input data-testid="etsy-shop" value={eshop} onChange={(e) => setEshop(e.target.value)} placeholder={etsy?.shop_id || "otomatik algılanır"} />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button data-testid="etsy-save-btn" onClick={saveEtsy} disabled={busy} className="bg-slate-900 hover:bg-slate-800">Anahtarları Kaydet</Button>
          <Button data-testid="etsy-connect-btn" onClick={connect} disabled={busy || !etsy?.configured} className="bg-blue-600 hover:bg-blue-700">
            <Link2 className="h-4 w-4 mr-1" />{etsy?.connected ? "Yeniden Bağla" : "Etsy'yi Bağla"}
          </Button>
          <Button data-testid="etsy-sync-btn" onClick={() => syncNow(false)} disabled={busy || !etsy?.connected} variant="outline">
            <RefreshCcw className="h-4 w-4 mr-1" />Şimdi Senkronla
          </Button>
          <Button data-testid="etsy-full-sync-btn" onClick={() => { if (window.confirm('Tüm geçmiş açık siparişler baştan taranacak (mükerrer eklemez). Devam edilsin mi?')) syncNow(true); }} disabled={busy || !etsy?.connected} className="bg-amber-600 hover:bg-amber-700 text-white">
            <RefreshCcw className="h-4 w-4 mr-1" />Baştan Tara
          </Button>
          <Button data-testid="etsy-backfill-btn" onClick={backfillImages} disabled={busy || !etsy?.connected} variant="outline">
            Görselleri İndir
          </Button>
          <Button data-testid="etsy-reconcile-btn" onClick={reconcile} disabled={busy || !etsy?.connected} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            Etsy ile Eşitle (Kargolananları Tamamla)
          </Button>
          <Button data-testid="orders-reset-btn" onClick={resetAndResync} disabled={busy || !etsy?.connected} className="bg-red-600 hover:bg-red-700 text-white">
            ⚠️ Sıfırla ve Baştan Çek (son 45g, kargolanmamış)
          </Button>
          <Button data-testid="etsy-log-btn" onClick={loadLog} variant="outline">
            Senkron Geçmişi
          </Button>
          {etsy?.connected && (
            <Button onClick={disconnect} variant="ghost" className="text-red-600 hover:text-red-700">
              <Unlink className="h-4 w-4 mr-1" />Bağlantıyı Kes
            </Button>
          )}
        </div>

        <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 leading-relaxed">
          <div className="font-bold text-slate-800 mb-1">Nasıl kurulur?</div>
          <ol className="list-decimal ml-4 space-y-1">
            <li><a className="text-blue-600 hover:underline" href="https://www.etsy.com/developers/your-apps" target="_blank" rel="noreferrer">etsy.com/developers/your-apps</a> adresinden ücretsiz uygulama oluşturun.</li>
            <li>Callback URL olarak birebir şunu ekleyin: <span className="font-mono bg-white border border-slate-200 rounded px-1">{etsy?.redirect_uri || "..."}</span></li>
            <li>Aldığınız <b>Keystring</b> ve <b>Shared Secret</b>'ı yukarıya girip <b>Kaydet</b>'e basın.</li>
            <li><b>Etsy'yi Bağla</b>'ya basıp Etsy hesabınızla onay verin — sistem her 15 dakikada bir siparişleri otomatik çeker.</li>
          </ol>
        </div>

        {syncLog !== null && (
          <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Son 20 Senkron</span>
              <button onClick={() => setSyncLog(null)} className="text-xs text-slate-500 hover:text-slate-800">Gizle</button>
            </div>
            {syncLog.length === 0 ? (
              <div className="p-4 text-xs text-slate-500">Henüz senkron kaydı yok.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {syncLog.map((l) => (
                  <div key={l.id} className={`px-4 py-2 text-xs flex items-start gap-3 ${l.status === "error" ? "bg-red-50/40" : ""}`}>
                    <span className={`shrink-0 mt-0.5 inline-block h-2 w-2 rounded-full ${l.status === "error" ? "bg-red-500" : "bg-emerald-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] text-slate-500">{new Date(l.at).toLocaleString("tr-TR")}</div>
                      <div className="text-slate-800">
                        {l.status === "error"
                          ? <span className="text-red-700 font-semibold">Hata: {l.error}</span>
                          : <>Kontrol edilen: <b>{l.checked}</b> · Yeni eklenen: <b>{l.imported}</b> · Sayfa: {l.pages}</>}
                      </div>
                      {l.detail && <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={l.detail}>{l.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6 border-slate-200 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="h-4 w-4 text-pink-600" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Boya</div>
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Boya Renk Paleti</h3>
        <p className="text-sm text-slate-500 mb-4">Üretim panosunda "Boya rengi belirle" ekranında hızlı seçim olarak görünür.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
          {paints.length === 0 && <div className="text-xs text-slate-500 col-span-full">Henüz renk yok — aşağıdan ekleyin.</div>}
          {paints.map((p) => (
            <div key={p.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-2 py-2 bg-white">
              <input
                type="color"
                value={p.hex}
                onChange={(e) => updatePaint(p, { hex: e.target.value })}
                className="h-9 w-11 rounded cursor-pointer border border-slate-200 shrink-0"
              />
              <Input
                defaultValue={p.label}
                onBlur={(e) => e.target.value.trim() && e.target.value !== p.label && updatePaint(p, { label: e.target.value.trim() })}
                className="flex-1 h-9 text-sm"
              />
              <Button size="icon" variant="ghost" onClick={() => delPaint(p)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Yeni Renk Ekle</div>
          <div className="flex gap-2">
            <input type="color" value={pHex} onChange={(e) => setPHex(e.target.value)} className="h-10 w-12 rounded cursor-pointer border border-slate-200" />
            <Input data-testid="paint-label-input" value={pLabel} onChange={(e) => setPLabel(e.target.value)} placeholder="Örn. RAL 9005 Mat, Antik Bronz..." className="flex-1" />
            <Button data-testid="paint-add-btn" onClick={addPaint} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" />Ekle</Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 mb-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">İmalat Aşamaları</h3>
        <p className="text-sm text-slate-500 mb-4">Sıra, üretim panosundaki (Kanban) sütun sırasını belirler.</p>

        <div className="space-y-2 mb-6">
          {stages.map((s, i) => (
            <div key={s.id} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2 bg-white">
              <GripVertical className="h-4 w-4 text-slate-300" />
              <input type="color" value={s.color} onChange={async (e) => { await api.put(`/stages/${s.id}`, { name: s.name, color: e.target.value }); loadStages(); }} className="h-8 w-10 rounded cursor-pointer border border-slate-200" />
              <Input defaultValue={s.name} onBlur={(e) => e.target.value !== s.name && rename(s, e.target.value)} className="flex-1" />
              <span className="text-xs text-slate-400 font-mono">#{i + 1}</span>
              <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === stages.length - 1}><ChevronDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(s)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Yeni Aşama Ekle</div>
          <div className="flex gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-12 rounded cursor-pointer border border-slate-200" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Aşama adı (örn. Boyama)" className="flex-1" />
            <Button onClick={add} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" />Ekle</Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-slate-200">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Diğer Entegrasyonlar (Yol Haritası)</h3>
        <p className="text-sm text-slate-500 mb-4">Bir sonraki fazda etkinleştirilecek pazaryeri ve iletişim entegrasyonları.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {["Trendyol", "Hepsiburada", "Shopify", "WhatsApp", "SMS Bildirim", "E-posta Bildirim"].map((x) => (
            <div key={x} className="border border-dashed border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-600 flex items-center justify-between">
              <span>{x}</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Yakında</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function StatusPill({ label, ok, okText, noText, value }) {
  const tone = value !== undefined ? "bg-slate-100 text-slate-700 border-slate-200"
    : ok ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-500 border-slate-200";
  const text = value !== undefined ? String(value) : (ok ? okText : noText);
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${tone}`}>
      <span className="uppercase tracking-widest font-bold text-[10px] opacity-70">{label}</span>
      <span className="font-semibold flex items-center gap-1">
        {ok && <CheckCircle2 className="h-3 w-3" />}{text}
      </span>
    </div>
  );
}
